import React from 'react';
import { styled } from '@mui/material/styles';
import { APICONSTANTS, CONSTANTS } from '../Constants';
import { Grid } from '@mui/material';
import PedigreeBox from './PedigreeBox';
import { AppContext } from 'src/store/ContextStore';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'src/axios';
import { EVENTS } from 'src/components/admin/appReducer';
import { usePedigreeLock } from 'src/hooks/usePedigreeLock';
import { usePersonLocks } from 'src/hooks/usePersonLocks';
import { useQueryClient } from '@tanstack/react-query';

const Container = styled(Grid)({
  position: 'absolute',
  height: '100%',
  // The v7 Grid `container` no longer stretches to full width by default
  // (unlike the legacy Grid), so set it explicitly — the pedigree canvas reads
  // its parent's clientWidth/clientHeight and renders blank at zero width.
  width: '100%',
  top: 0,
  left: 0,
  overflow: 'hidden !important'
});

function PedigreeChartView({ patient }) {
  const { content } = React.useContext(AppContext);
  const {
    pedigreeSelectedUsers: { selectedUser }
  } = content;

  const navigate = useNavigate();
  const { _id: familyId } = useParams();
  const { dispatchUpdate } = React.useContext(AppContext);

  const queryClient = useQueryClient();

  React.useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: ['probandFamilies'] });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    lockState,
    isMine,
    requestStatus,
    requestLock,
    cancelRequestLock,
    forceAcquireLock,
    pendingRequest,
    respondToRequest
  } = usePedigreeLock(familyId);

  const isLocked = !!(lockState && !isMine);

  const personLockHook = usePersonLocks(familyId);

  const getPedigreeDetails = () => {
    if (!familyId) return;
    // NOTE: deliberately does NOT drive the global LoadingContext / black BackdropLoader.
    // The Canvas renders its own "Loading family…" overlay while the worker computes the
    // layout; showing the global black spinner too produced a double spinner on open (the
    // backdrop's fade-out overlapped the Canvas overlay). This view's loading UX is the
    // Canvas overlay alone.
    axios
      // ?auditView=1 marks this as a genuine record OPEN (a Tier-2 access event),
      // distinct from the post-edit data reloads that hit the same endpoint.
      .get(
        `${APICONSTANTS.getPedigreeDetails.replace(
          ':familyId',
          familyId
        )}?auditView=1`
      )
      .then((resp) => {
        const { proband, families, perf } = resp.data;

        const value = {
          family: families,
          selectedUser: proband,
          perf
        };

        dispatchUpdate({ type: EVENTS.SET_PEDIGREE_SELECTED_USER, value });
      })
      .catch((err) => {
        navigate(CONSTANTS.pedigreeToolPath);
      });
  };

  React.useEffect(() => {
    getPedigreeDetails();
  }, [familyId]);

  const pedigree = React.useMemo(() => {
    return (
      <>
        {selectedUser?._id && (
          <PedigreeBox
            patient={patient}
            isLocked={isLocked}
            isMine={isMine}
            lockState={lockState}
            requestStatus={requestStatus}
            onRequestLock={requestLock}
            onCancelRequest={cancelRequestLock}
            onForceAcquireLock={forceAcquireLock}
            pendingRequest={pendingRequest}
            onRespondToRequest={respondToRequest}
            personLockHook={personLockHook}
          />
        )}
      </>
    );
  }, [
    patient,
    selectedUser,
    isLocked,
    isMine,
    lockState,
    requestStatus,
    pendingRequest,
    personLockHook
  ]);

  return <Container container>{pedigree}</Container>;
}

export default PedigreeChartView;
